import { Button } from 'blaze-ui/Button'
import Styles from './EditPostMenu.module.scss'
import { AddElements, Brand, Caption, Design, FileEdit2, Microphone, Plus } from 'blaze-ui/icons'
import { ArrowRefresh2 } from 'blaze-ui/icons/20/ArrowRefresh2'
import { useModals } from 'almanac-ui/Modal'
import { ScheduledPostShow } from 'api/clients/ScheduledPostsClient'
import { useQueryClient } from 'react-query'
import { channelTypeFromProvider, maxFiles } from 'components/ArticlePostPreview/ArticlePostPreviewContext'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useLocation, useHistory, generatePath, Link } from 'react-router-dom'
import { BRAND_KITS_PATH, DESIGN_PATH, DOC_PATH } from 'paths'
import { ImageMenuModal } from 'components/ImageMenu/ImageMenuModal/ImageMenuModal'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { MediaObject } from 'types'
import { useCreateMediaObjectsWithoutPresigning } from 'hooks/queries/mediaObjects/useCreateMediaObjects'
import { useMemo, useRef } from 'react'
import { useInsertMediaObjects } from 'hooks/queries/designs/useInsertMediaObjects'
import { ToolbarMenu } from '../Toolbar/ToolbarMenu'
import { ButtonLink } from 'blaze-ui/Button/ButtonLink'
import { EditCaptionButton } from './EditCaptionButton'
import { AssetType } from 'hooks/useAlmanacFileSubscription'
import { AlmanacFileShowItem } from 'hooks/queries/files/types'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useCurrentWorkspaceId } from 'hooks/useCurrentWorkspaceId'
import { AudioEditorModal } from 'components/Previews/components/VideoPlayer/AudioEditorModal'
import { RegenerateDesignModal } from 'components/RegenerateDesignModal/RegenerateDesignModal'
import { calendarKeys } from 'hooks/queries/calendar/calendarKeys'
import { removeFileDetailCache } from 'hooks/queries/files/cacheUtils'
import { useIsSystemRedesign } from 'hooks/useIsSystemRedesign'
import { DesignChannel } from 'components/NewFileModal/Types'
import { isStoryChannel } from '../utils'
import { ImageEditor } from '../ImageEditor/ImageEditor'
import { useShowNextGenContentPreferences } from 'hooks/useShowNextGenContentPreferences'
import { ButtonProps, LinkButtonLinkProps } from 'blaze-ui/Button/Types'
import { ReplaceDesignButton } from './ReplaceDesignButton'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'

export const EditPostMenu = observer(
  ({
    scheduledPost,
    almanacFile,
    previewPlatform,
    isBlankDesign,
    isLoading = false,
    isInsertingMedia = false,
    onLoadingChange,
    onClose,
  }: {
    scheduledPost: ScheduledPostShow
    almanacFile: AlmanacFileShowItem
    previewPlatform?: ExternalIntegrationEnum
    isBlankDesign: boolean
    isLoading?: boolean
    isInsertingMedia?: boolean
    onLoadingChange?: (isLoading: boolean) => void
    onClose?: () => void
  }) => {
    const { generationTimestampStore } = useRootStore()
    const { openModal, closeModal, closeAllModals } = useModals()
    const queryClient = useQueryClient()
    const location = useLocation()
    const history = useHistory()
    const { mutateAsync: createMediaObjectsWithoutPresigning } = useCreateMediaObjectsWithoutPresigning()
    const metadataRef = useRef<any[]>([])
    const dynamicCaptionsEnabled = useFlipperFlag('dynamic_captions')
    const { showNextGenContentPreferences: isGenV2 } = useShowNextGenContentPreferences()
    const workspaceId = useCurrentWorkspaceId()
    const isSystemRedesignEnabled = useIsSystemRedesign()

    const replaceFrameBatchId = generationTimestampStore.getReplaceFrameBatchId(almanacFile.key)
    const isReplacingFrame = !!replaceFrameBatchId
    const isGenerating = almanacFile.is_generating || isReplacingFrame
    const hideAddMedia = isGenerating || isLoading || isInsertingMedia || !!almanacFile.image_url_generating

    const { mutate: insertMediaObjects } = useInsertMediaObjects(
      {
        designId: almanacFile.key,
      },
      {
        onMutate: () => {
          onLoadingChange?.(true)
        },
        onSuccess: () => {
          queryClient.invalidateQueries(['calendarItems'])
          queryClient.invalidateQueries(['calendar'])
          if (almanacFile?.scheduled_post) {
            queryClient.invalidateQueries(['scheduledPosts', almanacFile.scheduled_post.id])
          }
        },
        onSettled: () => {
          onLoadingChange?.(false)
        },
      },
    )

    const handleMediaSelection = async (images: ImageMenuSelectedImage[]) => {
      const integration = scheduledPost.external_integration
      const maxFilesCount = integration ? maxFiles[integration.provider] : Infinity
      let newFiles: MediaObject[] = []

      // Process images sequentially with Promise.all to wait for all async operations
      await Promise.all(
        images.map(async (image, index) => {
          const { id, ...file } = image
          const mediaObjects = await createMediaObjectsWithoutPresigning([
            {
              ...file,
              ...metadataRef.current[index],
              url: file.mime_type?.startsWith('video') && file.source_url ? file.source_url : file.url,
            },
          ])
          newFiles.push(mediaObjects[0])
        }),
      )

      // Update the scheduled post with the new media object IDs
      const currentMediaObjectIds = scheduledPost?.media_objects?.map((obj) => obj.id) || []
      const newMediaObjectIds = newFiles.map((file) => file.id)
      const allMediaObjectIds = [...currentMediaObjectIds, ...newMediaObjectIds].slice(0, maxFilesCount)

      insertMediaObjects({
        media_object_ids: allMediaObjectIds,
      })
    }

    const handleAddMediaClick = () => {
      const integration = scheduledPost.external_integration
      const maxCount = integration
        ? maxFiles[integration.provider] - (scheduledPost?.media_objects?.length || 0)
        : Infinity

      openModal(
        ImageMenuModal,
        {
          maxCount,
          hiddenProviders: ['gifs'],
          uploadMedia: true,
          onUse: handleMediaSelection,
        },
        {
          newStack: true,
        },
      )
    }

    const handleOpenBrandKitStyles = () => {
      history.push({ pathname: generatePath(BRAND_KITS_PATH, { workspaceId }), hash: 'styles_voice' })
      closeModal()
    }

    const to = generatePath(DESIGN_PATH, { key: almanacFile.key })

    const handleVisualClick = () => {
      const isOnDesignPath = location.pathname === to

      if (isOnDesignPath) {
        closeModal()
      } else {
        onClose?.()
        closeModal()
        history.push(to)
      }
    }

    const panel = useMemo(() => {
      if (almanacFile.asset_type === AssetType.Article) {
        return 'document'
      }

      if (isBlankDesign) {
        return 'blank'
      }

      return 'default'
    }, [isBlankDesign, almanacFile.asset_type])

    const imageMediaObjects = (scheduledPost?.media_objects ?? []).filter(
      (obj: MediaObject) => obj.mime_type?.startsWith('image/') && obj.source_url && obj.width && obj.height,
    )

    const shouldHideCaptionField =
      isStoryChannel(scheduledPost?.source_channel as DesignChannel) ||
      isStoryChannel(almanacFile?.channel as DesignChannel)

    const handleAudioTransformComplete = () => {
      removeFileDetailCache(queryClient, workspaceId, almanacFile.key)
      queryClient.invalidateQueries({ queryKey: calendarKeys.detail(almanacFile.key) })
    }

    const videoMediaObject = scheduledPost?.media_objects?.find((object) => object.mime_type?.startsWith('video/'))
    const handleOpenAudioEditor = () => {
      if (!videoMediaObject) return
      openModal(
        AudioEditorModal,
        {
          mediaObject: videoMediaObject,
          almanacFileKey: almanacFile.key,
          onAudioTransformComplete: handleAudioTransformComplete,
        },
        { newStack: true },
      )
    }

    const handleOpenRegenerateContent = () => {
      openModal(RegenerateDesignModal, { almanacFile }, { newStack: true })
    }

    return (
      <ToolbarMenu title="Improve" height={isSystemRedesignEnabled ? 20 : undefined}>
        <div className={Styles.buttons}>
          {panel === 'document' && (
            <ActionButton
              frontIcon={FileEdit2}
              onPress={() => {
                history.push(generatePath(DOC_PATH, { slug: almanacFile.key }))
                setTimeout(() => {
                  onClose?.()
                }, 100)
              }}
            >
              Edit Document
            </ActionButton>
          )}

          {panel === 'blank' && (
            <>
              {!shouldHideCaptionField && (
                <div>
                  <EditCaptionButton
                    scheduledPost={scheduledPost}
                    almanacFileKey={almanacFile.key}
                    variant="tertiary"
                    size="md"
                    frontIcon={FileEdit2}
                    justifyContent="flex-start"
                    vertical
                    shouldHideCaptionField={shouldHideCaptionField}
                    label={dynamicCaptionsEnabled ? 'Captions' : 'Caption'}
                    fullWidth={false}
                  />
                </div>
              )}
              {!hideAddMedia && (
                <Button
                  justifyContent="flex-start"
                  variant={isSystemRedesignEnabled ? 'secondary' : 'ghost-tertiary'}
                  size="md"
                  frontIcon={Plus}
                  onPress={handleAddMediaClick}
                  vertical
                >
                  Add Media
                </Button>
              )}
            </>
          )}

          {panel === 'default' && (
            <>
              {!isGenV2 && location.pathname === to && (
                <ActionButton frontIcon={Design} onPress={handleVisualClick}>
                  Design
                </ActionButton>
              )}
              {!isGenV2 && location.pathname !== to && (
                <ActionButtonLink
                  frontIcon={Design}
                  elementType={Link}
                  to={to}
                  onPress={() => {
                    setTimeout(() => {
                      onClose?.()
                    }, 100)
                  }}
                  isDisabled={isGenerating}
                >
                  Design
                </ActionButtonLink>
              )}

              {!shouldHideCaptionField && (
                <div>
                  <EditCaptionButton
                    scheduledPost={scheduledPost}
                    almanacFileKey={almanacFile.key}
                    variant="tertiary"
                    size="md"
                    frontIcon={Caption}
                    justifyContent="center"
                    vertical={false}
                    shouldHideCaptionField={shouldHideCaptionField}
                    isDisabled={isGenerating}
                    label={dynamicCaptionsEnabled ? 'Captions' : 'Caption'}
                    fullWidth={false}
                  />
                </div>
              )}

              {isGenV2 && imageMediaObjects.length > 0 && (
                <ActionButton
                  frontIcon={Design}
                  isDisabled={isGenerating}
                  onPress={() => {
                    openModal(
                      ImageEditor,
                      {
                        designKey: almanacFile.key,
                        mediaObjects: imageMediaObjects,
                        channel: (previewPlatform
                          ? channelTypeFromProvider[previewPlatform]
                          : almanacFile.channel) as DesignChannel,
                        integration: scheduledPost.external_integration,
                        caption: scheduledPost.content,
                      },
                      { newStack: true },
                    )
                  }}
                >
                  Edit elements With AI
                </ActionButton>
              )}

              {isGenV2 && <ReplaceDesignButton almanacFile={almanacFile} onLoadingChange={onLoadingChange} />}

              {videoMediaObject && (
                <ActionButton frontIcon={Microphone} isDisabled={isGenerating} onPress={handleOpenAudioEditor}>
                  Audio
                </ActionButton>
              )}

              {!isGenV2 && (
                <ActionButton frontIcon={Brand} onPress={handleOpenBrandKitStyles}>
                  Brand
                </ActionButton>
              )}

              {isGenV2 && (
                <ActionButtonLink
                  frontIcon={AddElements}
                  elementType={Link}
                  to={generatePath(DESIGN_PATH, { key: almanacFile.key })}
                  onPress={() => {
                    closeAllModals()
                  }}
                >
                  Add logos, stickers & text
                </ActionButtonLink>
              )}

              <ActionButton frontIcon={ArrowRefresh2} isDisabled={isGenerating} onPress={handleOpenRegenerateContent}>
                Regenerate
              </ActionButton>
            </>
          )}
        </div>
      </ToolbarMenu>
    )
  },
)

export const ActionButton = (props: ButtonProps) => (
  <div>
    <Button variant="tertiary" size="md" {...props} />
  </div>
)
const ActionButtonLink = ({ to, elementType, ...props }: LinkButtonLinkProps) => (
  <div>
    <ButtonLink variant="tertiary" size="md" to={to} elementType={elementType} {...props} />
  </div>
)
